import posthog, { type Properties } from 'posthog-js';
import { AnalyticsConsentService } from './AnalyticsConsentService';

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY || '';
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://eu.i.posthog.com';
const POSTHOG_ENABLED = String(process.env.REACT_APP_POSTHOG_ENABLED || 'false').toLowerCase() === 'true';
const POSTHOG_DEFAULTS = '2026-01-30';
const DEBUG_QUERY_PARAM = 'ts_posthog_debug';

export interface PostHogDebugInfo {
  configured: boolean;
  enabled: boolean;
  host: string;
  defaults: string;
  keyPreview: string;
  consentState: 'unknown' | 'granted' | 'denied';
  bootEventSent: boolean;
  pageViewSent: boolean;
  lastPageViewPath: string | null;
  distinctIdPreview: string;
  identifiedUserPreview: string;
  optedOut: boolean;
  recordingActive: boolean;
  debugMode: boolean;
}

const BOOT_EVENT_SESSION_KEY = 'ts_posthog_boot_event_sent_v1';
const PAGEVIEW_SESSION_KEY = 'ts_posthog_last_pageview_path_v1';

const hasBrowser = (): boolean => typeof window !== 'undefined' && typeof document !== 'undefined';

const isDebugMode = (): boolean => {
  if (!hasBrowser()) return false;
  return new URLSearchParams(window.location.search).get(DEBUG_QUERY_PARAM) === '1';
};

const normalizePath = (path?: string): string => {
  if (!hasBrowser()) return path || '/';
  return path || `${window.location.pathname || '/'}${window.location.search || ''}`;
};

const toAbsoluteUrl = (path: string): string => {
  if (!hasBrowser()) return path;
  if (/^https?:\/\//i.test(path)) return path;
  return `${window.location.origin}${path.startsWith('/') ? path : `/${path}`}`;
};

export class PostHogService {
  private static initialized = false;
  private static lastTrackedPagePath: string | null = null;
  private static identifiedUserId: string | null = null;
  private static unsubscribeConsent: (() => void) | null = null;

  private static trackBootEvent(): void {
    if (typeof window === 'undefined') return;
    if (sessionStorage.getItem(BOOT_EVENT_SESSION_KEY) === '1') return;

    posthog.capture('ops_browser_booted', {
      source: 'browser_init',
      current_path: window.location.pathname,
      current_host: window.location.host,
      locale: document.documentElement.lang || undefined
    });

    sessionStorage.setItem(BOOT_EVENT_SESSION_KEY, '1');
  }

  static isConfigured(): boolean {
    return POSTHOG_ENABLED && Boolean(POSTHOG_KEY);
  }

  static isEnabled(): boolean {
    return this.isConfigured() && hasBrowser() && AnalyticsConsentService.hasGranted();
  }

  static getDebugInfo(): PostHogDebugInfo {
    const posthogInstance = posthog as any;
    return {
      configured: this.isConfigured(),
      enabled: this.isEnabled(),
      host: POSTHOG_HOST,
      defaults: POSTHOG_DEFAULTS,
      keyPreview: POSTHOG_KEY ? `${POSTHOG_KEY.slice(0, 6)}...${POSTHOG_KEY.slice(-4)}` : 'missing',
      consentState: AnalyticsConsentService.getState(),
      bootEventSent: typeof window !== 'undefined' && sessionStorage.getItem(BOOT_EVENT_SESSION_KEY) === '1',
      pageViewSent: typeof window !== 'undefined' && Boolean(sessionStorage.getItem(PAGEVIEW_SESSION_KEY)),
      lastPageViewPath: typeof window !== 'undefined' ? sessionStorage.getItem(PAGEVIEW_SESSION_KEY) : null,
      distinctIdPreview: typeof posthogInstance.get_distinct_id === 'function'
        ? String(posthogInstance.get_distinct_id() || 'missing').slice(0, 12)
        : 'missing',
      identifiedUserPreview: this.identifiedUserId ? `${this.identifiedUserId.slice(0, 6)}...${this.identifiedUserId.slice(-4)}` : 'missing',
      optedOut: typeof posthogInstance.has_opted_out_capturing === 'function'
        ? Boolean(posthogInstance.has_opted_out_capturing())
        : false,
      recordingActive: typeof posthogInstance.sessionRecordingStarted === 'function'
        ? Boolean(posthogInstance.sessionRecordingStarted())
        : false,
      debugMode: isDebugMode()
    };
  }

  static bootstrap(): void {
    if (!this.isConfigured() || !hasBrowser()) return;

    if (!this.unsubscribeConsent) {
      this.unsubscribeConsent = AnalyticsConsentService.subscribe((state) => {
        if (state === 'granted') {
          this.init();
        } else if (state === 'denied') {
          this.disable();
        }
      });
    }

    if (AnalyticsConsentService.hasGranted()) {
      this.init();
    } else {
      this.disable();
    }
  }

  static init(): void {
    if (this.initialized || !this.isEnabled()) return;

    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      defaults: POSTHOG_DEFAULTS,
      person_profiles: 'identified_only',
      opt_out_capturing_by_default: true,
      capture_pageview: false,
      autocapture: false,
      disable_surveys: true,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: 'input, textarea'
      },
      debug: isDebugMode(),
      loaded: () => {
        const posthogInstance = posthog as any;
        if (typeof posthogInstance.opt_in_capturing === 'function') {
          posthogInstance.opt_in_capturing();
        }
        this.trackBootEvent();
        this.initialized = true;
      }
    });

    const posthogInstance = posthog as any;
    if (typeof posthogInstance.opt_in_capturing === 'function') {
      posthogInstance.opt_in_capturing();
    }

    this.initialized = true;
  }

  static identify(userId: string, traits?: Properties): void {
    if (!this.isEnabled() || !userId) return;
    this.init();
    posthog.identify(userId, traits);
    this.identifiedUserId = userId;
  }

  static reset(): void {
    if (!hasBrowser()) return;
    posthog.reset();
    this.initialized = false;
    this.identifiedUserId = null;
  }

  static disable(): void {
    if (!hasBrowser() || !this.initialized) return;

    const posthogInstance = posthog as any;
    if (typeof posthogInstance.stopSessionRecording === 'function') {
      posthogInstance.stopSessionRecording();
    }
    if (typeof posthogInstance.opt_out_capturing === 'function') {
      posthogInstance.opt_out_capturing();
    }
    posthog.reset();
    this.initialized = false;
  }

  static capture(eventName: string, properties: Properties = {}): void {
    if (!this.isEnabled()) return;
    this.init();
    posthog.capture(eventName, properties);
  }

  static trackPageView(path?: string, title?: string, options: { force?: boolean; source?: string } = {}): void {
    if (!this.isEnabled()) return;

    const normalizedPath = normalizePath(path);
    if (!options.force && this.lastTrackedPagePath === normalizedPath) return;

    this.init();
    posthog.capture('$pageview', {
      $current_url: toAbsoluteUrl(normalizedPath),
      $pathname: normalizedPath.split('?')[0] || '/',
      $title: title || (hasBrowser() ? document.title : undefined),
      source: options.source || 'spa_route',
      current_path: normalizedPath,
      debug_mode: isDebugMode()
    });

    this.lastTrackedPagePath = normalizedPath;
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(PAGEVIEW_SESSION_KEY, normalizedPath);
    }
  }

  static sendManualDebugEvent(): { success: boolean; message: string } {
    if (!this.isConfigured()) {
      return { success: false, message: 'PostHog non configurato' };
    }

    if (!AnalyticsConsentService.hasGranted()) {
      return { success: false, message: 'Consenso analytics non concesso' };
    }

    this.init();
    this.trackPageView(undefined, hasBrowser() ? document.title : undefined, {
      force: true,
      source: 'admin_platform_debug'
    });
    posthog.capture('ops_manual_browser_debug', {
      source: 'admin_platform_debug',
      current_path: typeof window !== 'undefined' ? window.location.pathname : undefined,
      current_host: typeof window !== 'undefined' ? window.location.host : undefined,
      current_url: typeof window !== 'undefined' ? window.location.href : undefined,
      debug_mode: isDebugMode(),
      sent_at: new Date().toISOString()
    });

    return { success: true, message: 'Evento browser inviato: ops_manual_browser_debug' };
  }

  static startManualSessionReplay(): { success: boolean; message: string } {
    if (!this.isConfigured()) {
      return { success: false, message: 'PostHog non configurato' };
    }

    if (!AnalyticsConsentService.hasGranted()) {
      return { success: false, message: 'Consenso analytics non concesso' };
    }

    this.init();

    const posthogInstance = posthog as any;
    if (typeof posthogInstance.startSessionRecording === 'function') {
      posthogInstance.startSessionRecording();
      posthog.capture('ops_manual_session_replay_requested', {
        source: 'admin_platform_debug',
        current_path: typeof window !== 'undefined' ? window.location.pathname : undefined,
        sent_at: new Date().toISOString()
      });
      return { success: true, message: 'Session replay richiesta: ops_manual_session_replay_requested' };
    }

    return { success: false, message: 'SDK PostHog senza startSessionRecording disponibile' };
  }
}
